import { Link } from "react-router-dom";
import { Facebook, Instagram, Twitter, Phone, Mail, MapPin } from "lucide-react";
import LogoText from "./LogoText";
import { useSiteConfig } from "@/hooks/useSiteConfig";

import GTranslateWidget from "./GTranslateWidget";

const Footer = () => {
  const { getConfig } = useSiteConfig();
  
  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-4">
        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <LogoText useFooterLogo={true} className="h-16" />
            <p className="text-primary-foreground/80 text-sm leading-relaxed">
              {getConfig('footer_description', 'Your premier transfer and tour service in Punta Cana. Experience the Caribbean with comfort, reliability, and style.')}
            </p>
            <div className="flex space-x-4">
              <GTranslateWidget />
            </div>
            <div className="flex space-x-4 mt-4">
              {getConfig('social_facebook')?.enabled && (
                <a 
                  href={getConfig('social_facebook')?.url || '#'} 
                  className="text-primary-foreground/60 hover:text-secondary transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {getConfig('social_instagram')?.enabled && (
                <a 
                  href={getConfig('social_instagram')?.url || '#'} 
                  className="text-primary-foreground/60 hover:text-secondary transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {getConfig('social_tiktok')?.enabled && (
                <a 
                  href={getConfig('social_tiktok')?.url || '#'} 
                  className="text-primary-foreground/60 hover:text-secondary transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Twitter className="h-5 w-5" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-6">
            <h3 className="font-bold text-lg uppercase tracking-wide">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-primary-foreground/80 hover:text-secondary transition-colors text-sm">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-primary-foreground/80 hover:text-secondary transition-colors text-sm">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/transfers" className="text-primary-foreground/80 hover:text-secondary transition-colors text-sm">
                  Transfers
                </Link>
              </li>
              <li>
                <Link to="/tours" className="text-primary-foreground/80 hover:text-secondary transition-colors text-sm">
                  Tours
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-primary-foreground/80 hover:text-secondary transition-colors text-sm">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-primary-foreground/80 hover:text-secondary transition-colors text-sm">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-primary-foreground/80 hover:text-secondary transition-colors text-sm">
                  FAQ
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div className="space-y-6">
            <h3 className="font-bold text-lg uppercase tracking-wide">Our Services</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/transfers" className="text-primary-foreground/80 hover:text-secondary transition-colors text-sm">
                  Airport Transfers
                </Link>
              </li>
              <li>
                <Link to="/transfers" className="text-primary-foreground/80 hover:text-secondary transition-colors text-sm">
                  Hotel Transfers
                </Link>
              </li>
              <li>
                <Link to="/tours" className="text-primary-foreground/80 hover:text-secondary transition-colors text-sm">
                  Private Tours
                </Link>
              </li>
              <li>
                <Link to="/tours" className="text-primary-foreground/80 hover:text-secondary transition-colors text-sm">
                  Group Tours
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <h3 className="font-bold text-lg uppercase tracking-wide">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3 text-sm">
                <Phone className="h-5 w-5 text-secondary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-primary-foreground/80">{getConfig('contact_phone', '+1 (809) 123-4567')}</p>
                  <p className="text-primary-foreground/60 text-xs">24/7 Available</p>
                </div>
              </li>
              <li className="flex items-start space-x-3 text-sm">
                <Mail className="h-5 w-5 text-secondary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-primary-foreground/80">{getConfig('contact_email', 'info@dominicansol.com')}</p>
                  <p className="text-primary-foreground/60 text-xs">Email Support</p>
                </div>
              </li>
              <li className="flex items-start space-x-3 text-sm">
                <MapPin className="h-5 w-5 text-secondary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-primary-foreground/80">Punta Cana</p>
                  <p className="text-primary-foreground/60 text-xs">Dominican Republic</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-primary-light/20 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-primary-foreground/60 text-sm">
              © {new Date().getFullYear()} Dominican Sol Transfers. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <Link to="/terms" className="text-primary-foreground/60 hover:text-secondary transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="text-primary-foreground/60 hover:text-secondary transition-colors">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
